

import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, DatePicker, Row, Col, Space, Typography, message, Modal } from 'antd';
import axiosInstance from '../../../axiosInstance';

const { Option } = Select;
const { Title } = Typography;

const PurchaseOrder = () => {
    const [form] = Form.useForm();
    const [suppliers, setSuppliers] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [products, setProducts] = useState([{ id: '', quantity: '', buying_price: '', selling_price: '' }]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                const response = await axiosInstance.get('/vendors');
                setSuppliers(response.data.vendors);
            } catch (error) {
                message.error("Failed to fetch suppliers.");
            }
        };

        const fetchProducts = async () => {
            try {
                const response = await axiosInstance.get('/products');
                setProductsList(response.data.products);
            } catch (error) {
                message.error("Failed to fetch products.");
            }
        };

        fetchSuppliers();
        fetchProducts();
    }, []);

    const handleProductChange = (index, field, value) => {
        const updatedProducts = [...products];
        updatedProducts[index][field] = value;
        setProducts(updatedProducts);
    };

    const addProductRow = () => {
        setProducts([...products, { id: '', quantity: '', buying_price: '', selling_price: '' }]);
    };

    const removeProductRow = (index) => {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
    };

    const confirmSubmit = (values) => {
        Modal.confirm({
            title: 'Confirm Order Submission',
            content: 'Are you sure you want to submit this purchase order?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: () => handleSubmit(values),
        });
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        const payload = {
            supplier_id: values.supplier || '',
            order_date: values.order_date.format('YYYY-MM-DD'),
            order_type: values.order_type,
            products: products.map((product) => ({
                id: product.id,
                quantity: product.quantity,
                buying_price: product.buying_price,
                selling_price: product.selling_price
            })),
        };

        try {
            await axiosInstance.post('/purchase-orders', payload);
            message.success("Purchase Order Created Successfully!");
            form.resetFields();
            setProducts([{ id: '', quantity: '', buying_price: '', selling_price: '' }]);
        } catch (error) {
            message.error("Error creating Purchase Order.");
        } finally {
            setLoading(false);
        }
    };

    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'id',
            render: (_, record, index) => (
                <Select
                    showSearch
                    value={record.id}
                    onChange={(value) => handleProductChange(index, 'id', value)}
                    style={{ width: '200px' }}
                    placeholder="Select Product"
                    optionFilterProp="children" // Enables search based on the Option's text
                    filterOption={(input, option) =>
                        option?.children.toLowerCase().includes(input.toLowerCase())
                    } // Custom filter logic for better search
                >
                    {productsList.map((prod) => (
                        <Option key={prod.id} value={prod.id}>
                            {prod.name}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (_, record, index) => (
                <Input
                    required={true}
                    type="number"
                    value={record.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    // min={1}
                    step="0.00"
                    placeholder="Enter Quantity"
                />
            ),
        },
        {
            title: 'Buying Price',
            dataIndex: 'buying_price',
            render: (_, record, index) => (
                <Input
                    required={true}
                    type="number"
                    value={record.buying_price}
                    onChange={(e) => handleProductChange(index, 'buying_price', parseFloat(e.target.value) || 0)}
                    min={0}
                    placeholder="Enter Price"
                />
            ),
        },
        {
            title: 'Selling Price',
            dataIndex: 'selling_price',
            render: (_, record, index) => (
                <Input
                    // required={true}
                    type="number"
                    value={record.selling_price}
                    onChange={(e) => handleProductChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
                    min={0}
                    placeholder="Enter Price"
                />
            ),
        },
        {
            title: 'Total',
            render: (_, record) => (
                (record.quantity * record.buying_price).toFixed(2)
            ),
        },
        {
            title: 'Action',
            render: (_, __, index) => (
                index > 0 && (
                    <Button
                        type="link"
                        danger
                        onClick={() => removeProductRow(index)}
                    >
                        Remove
                    </Button>
                )
            ),
        },
    ];

    const calculateTotal = () => {
        let totalQuantity = 0;
        let totalPrice = 0;

        products.forEach(product => {
            totalQuantity += product.quantity;
            totalPrice += product.quantity * product.buying_price;
        });

        return { totalQuantity, totalPrice };
    };

    const { totalQuantity, totalPrice } = calculateTotal();

    return (
        <div>
            <Title level={3}>Create Purchase Order</Title>
            <Form
                form={form}
                layout="vertical"
                onFinish={confirmSubmit}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            label="Supplier"
                            name="supplier"
                            rules={[{ required: false, message: 'Please select a supplier' }]} >
                            <Select placeholder="Select Supplier">
                                {suppliers.map((supplier) => (
                                    <Option key={supplier.id} value={supplier.id}>
                                        {supplier.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            label="Order Date"
                            name="order_date"
                            rules={[{ required: true, message: 'Please select an order date' }]} >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={8}>
                        <Form.Item
                            label="Order Type"
                            name="order_type"
                            initialValue="local"
                            rules={[{ required: true, message: 'Please select an order type' }]} >
                            <Select>
                                <Option value="local">Local</Option>
                                <Option value="import">Import</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Table
                    dataSource={products}
                    columns={productColumns}
                    pagination={false}
                    rowKey={(record, index) => index}
                    style={{ marginBottom: '20px' }}
                    scroll={{ x: true }} // Ensures table is scrollable on smaller screens
                />

                <Space style={{ marginBottom: '20px', display: 'block', textAlign: 'center' }}>
                    <Button type="dashed" onClick={addProductRow}>
                        Add Product
                    </Button>
                </Space>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <strong>Total Quantity:</strong> {totalQuantity}
                    </Col>
                    <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
                        <strong>Total Price:</strong> {totalPrice.toFixed(2)}
                    </Col>
                </Row>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block style={{ margin: '0 auto', width: '200px' }}>
                        Submit Order
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default PurchaseOrder;

